import ReactEChartsCore from 'echarts-for-react/lib/core';
import {EChartsInstance} from "echarts-for-react/lib/types";
import {TableOpts, TableResponse, WithAPI} from "../api/temperature";
import {createContext, createRef} from "preact";
import {useContext, useEffect, useRef} from "preact/hooks";
import {TemperatureContext, useTemperature} from "./generic/temperature";
import {CSSContext, ThemeContext} from "./types";
import {FetchOpts, WithSensor} from "./pages/sensorDetail";
import {useFetchAndRender} from "./fetchAndRender";
import {useIntl} from "react-intl";

import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import { TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, VisualMapComponent } from "echarts/components";
import { LabelLayout, UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import {EChartOption} from "echarts";
import {useLoader} from "./generic/loader";
import styled from "styled-components";
import {lighten} from "polished";

echarts.use([
    LineChart,
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LabelLayout,
    UniversalTransition,
    CanvasRenderer,
    VisualMapComponent,
]);

export type DashboardChartProps = {
} & WithAPI & WithSensor;

export type ChartContext = {
    min: number | null;
    max: number | null;
    setMin: (newMin: number) => void;
    setMax: (newMax: number) => void;
};

export const SynchronizedCharts = createContext<ChartContext>({
    min: null,
    max: null,
    setMin: (newMin: number) => {},
    setMax: (newMax: number) => {}
});

export function DashboardChart(props: DashboardChartProps) {
    const temp = useTemperature();
    const tempCtx = useContext(TemperatureContext);

    const intl = useIntl();

    const chartRef = useRef<EChartsInstance>();

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chart = chartRef.current;
        const series: EChartOption.SeriesLine = ((chart.getOption() as EChartOption).series as EChartOption.Series[])[0] as EChartOption.SeriesLine;
        const data = ((series.data ?? []) as [Date, number, number][]).map(
            ([dt, _, raw]) => [dt, tempCtx.converter(raw), raw]);

        chart.setOption({
            series: [{
                data: data
            }]
        });
    }, [tempCtx]);

    const getOptions = (chartContext: ChartContext, style: CSSStyleDeclaration): EChartOption => {
        const options: EChartOption = {
            series: [{
                type: "line",
                showSymbol: false,
                smooth: true,
                areaStyle: {
                    opacity: .2,
                },
                name: props.sensor.name,
            }],
            legend: {
                show: false,
            },
            yAxis: {
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    showMinLabel: false,
                    showMaxLabel: true,
                    interval: 0,
                    inside: true,
                    textShadowColor: "black",
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    textShadowBlur: 2,
                    backgroundColor: style?.backgroundColor,
                    padding: [2, 5],
                    borderRadius: 2,
                    margin: 0,
                },
                splitNumber: 3,
                splitLine: {
                    lineStyle: {
                        color: style?.borderColor,
                    }
                },
                boundaryGap: false,
                zlevel: 100,
                min: (value): number => {
                    if (Number.isNaN(value.min)) {
                        return chartContext.min ?? 0;
                    }

                    const newMin = Math.min(
                        Math.floor(value.min / 10) * 10,
                        tempCtx.converter(0)
                    );

                    if (chartContext.min === null || newMin < chartContext.min) {
                        chartContext.setMin(newMin);
                    }

                    return chartContext.min ?? newMin;
                },
                max: (value): number => {
                    if (Number.isNaN(value.max)) {
                        return chartContext.max ?? 1;
                    }

                    const newMax = Math.max(
                        Math.ceil(value.max / 10) * 10,
                        tempCtx.converter(0)
                    );

                    if (chartContext.max === null || newMax > chartContext.max) {
                        chartContext.setMax(newMax);
                    }

                    return chartContext.max ?? newMax;
                },
            },
            xAxis: {
                type: "time",
                splitLine: {
                    show: false,
                },
                axisLine: {
                    show: false,
                },
                axisTick: {
                    lineStyle: {
                        color: style?.borderColor,
                    },
                },
                axisLabel: {
                    formatter: (value: number) => intl.formatTime(value, {hour: "numeric", minute: "numeric"}),
                    showMinLabel: false,
                    showMaxLabel: false,
                    inside: true,
                    margin: 2,
                    textShadowColor: "black",
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 1,
                    textShadowBlur: 2,
                },
                zlevel: 100,
            },
            visualMap: [{
                pieces: [
                    {
                        max: Number.MAX_SAFE_INTEGER,
                        min: tempCtx.converter(0),
                        color: "#00c90d"
                    },
                    {
                        max: tempCtx.converter(0),
                        min: Number.MIN_SAFE_INTEGER,
                        color: "#0083c9"
                    },
                ],
                outOfRange: {
                    color: "#00c90d"
                },
                show: false,
                dimension: 1,
            }],
            tooltip: {
                trigger: "axis",
                backgroundColor: window.getComputedStyle(document.body).backgroundColor,
                borderColor: style.borderColor,
                textStyle: {
                    color: window.getComputedStyle(document.body).color,
                    fontStyle: style.fontStyle as "normal" | "italic" | "oblique",
                    fontWeight: style.fontWeight as "normal" | "bold" | "bolder" | "lighter" | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800| 900,
                    fontFamily: style.fontFamily,
                    fontSize: style.fontSize as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
                    lineHeight: style.lineHeight as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
                },
                formatter: (params) => {
                    if (!Array.isArray(params)) {
                        params = [params];
                    }

                    const formatSeriesForTooltip = (series: EChartOption.Tooltip.Format) =>
                        `${series.marker}&nbsp;${series.seriesName}:&nbsp;${temp.format(((series.value ?? [0,0]) as number[])[1])}`;

                    return `<b>${intl.formatDate(params[0].axisValue)} ${intl.formatTime(params[0].axisValue)}</b><br />${params.map((series) => 
                        formatSeriesForTooltip(series)).join("<br />")
                    }`;
                }
            },
            grid: {
                left: 0,
                top: 5,
                right: 0,
                bottom: 0
            },
            textStyle: {
                color: style?.color,
                fontStyle: style?.fontStyle as "normal" | "italic" | "oblique",
                fontWeight: style?.fontWeight as "normal" | "bold" | "bolder" | "lighter" | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800| 900,
                fontFamily: style?.fontFamily,
                fontSize: style?.fontSize as unknown as number, // Probably not, but whatever, echarts typing is weird.
                lineHeight: style?.lineHeight as unknown as number, // Probably not, but whatever, echarts typing is weird.
            },
            animation: false,
        };

        console.log(options);

        return options;
    }

    const [showLoader, hideLoader] = useLoader();

    const refreshFullChart = (chart: EChartsInstance) => {
        // Fetch historical data.
        showLoader();
        props.api.table(props.sensor, { granularity: "every-minute", interval: "48h" })
            .then((json) => {
                let opts: EChartOption<EChartOption.SeriesLine> = {
                    series: [{
                        // @ts-ignore  As the typing is wrong, X axis can have date instance as valid argument.
                        data: json.table.map((row) => [
                            new Date(row.from),
                            tempCtx.converter(row.average),
                            row.average
                        ])
                    }],
                }
                chart.setOption(opts);
            })
            .finally(() => {
                chart.hideLoading();
                hideLoader();
            });
    }

    return <SynchronizedCharts.Consumer>{(chartContext) =>
        <CSSContext.Consumer>{({style, clientWidth, clientHeight}) => {
            if (style && clientWidth && clientHeight) {
                return <ReactEChartsCore
                    echarts={echarts}
                    onChartReady={(instance: EChartsInstance) => {
                        instance.showLoading("default", {
                            "color": style?.borderColor,
                            "textColor": style?.borderColor,
                            "maskColor": "rgba(255, 255, 255, 0)",
                        });
                        refreshFullChart(instance);
                        chartRef.current = instance;
                    }}
                    option={getOptions(chartContext, style)}
                    notMerge={false}
                    style={{
                        width: `${clientWidth}px`,
                        height: `${clientHeight}px`
                    }}
                    opts={{
                        height: clientHeight,
                        width: clientWidth
                    }}
                />
            } else {
                return null;
            }
        }}</CSSContext.Consumer>
    }</SynchronizedCharts.Consumer>;
}

type HistoryChartProps = {
    tableInterval?: string;
    showSamples?: boolean;
    showMin?: boolean;
    showMax?: boolean;
    showAverage?: boolean;
    title?: string
} & WithAPI & WithSensor & FetchOpts & Omit<TableOpts, "interval">;

const HistoryChartDefaults: Partial<HistoryChartProps> = {
    showSamples: true,
    showMin: true,
    showMax: true,
    showAverage: true
};

const HistoryChartContainer = styled.div`
    min-height: 15rem;
    border-color: ${props => lighten(.1, props.theme.colors.cardBg)};
    margin-bottom: 1rem;
    font-size: .8rem;
`;

export function HistoryChart(props: HistoryChartProps) {
    const intl = useIntl();
    const temp = useTemperature();

    const {interval, ...propsWithoutInterval} = props;
    const [result] = useFetchAndRender<TableResponse>(() => props.api.table(
        props.sensor,
        Object.assign({interval: props.tableInterval}, propsWithoutInterval)
    ), {
        interval
    });

    const propsWithDefault = Object.assign({}, HistoryChartDefaults, props);

    const options = (temperatureContext: TemperatureContext, style: CSSStyleDeclaration): EChartOption => {
        const opts: EChartOption = {
            legend: {
                show: false,
            },
            title: {
                text: props.title,
                show: true,
                textStyle: {
                    fontWeight: "bold",
                    color: "white",
                },
                top: "middle",
                left: "center"
            },
            yAxis: [
                {
                    name: temperatureContext.unit,
                    nameLocation: "middle",
                    nameGap: 30,
                    splitLine: {
                        lineStyle: {
                            color: style.borderColor,
                        }
                    },
                    axisTick: {
                        lineStyle: {
                            color: style.borderColor,
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: style.borderColor,
                        },
                    },
                    min: (value) => Math.min(Math.floor(value.min / 10) * 10, temperatureContext.converter(0)),
                    max: (value) => Math.max(Math.ceil(value.max / 10) * 10, temperatureContext.converter(0)),
                },
            ],
            xAxis: {
                type: "time",
                splitLine: {
                    lineStyle: {
                        color: style.borderColor,
                    },
                    show: true,
                },
                  axisTick: {
                    lineStyle: {
                        color: style.borderColor,
                    },
                },
                axisLine: {
                    lineStyle: {
                        color: style.borderColor,
                    },
                },
                axisLabel: {
                    showMaxLabel: true
                }
            },
            tooltip: {
                trigger: "axis",
                backgroundColor: window.getComputedStyle(document.body).backgroundColor,
                borderColor: style.borderColor,
                textStyle: {
                    color: style.color,
                    fontStyle: style.fontStyle as "normal" | "italic" | "oblique",
                    fontWeight: style.fontWeight as "normal" | "bold" | "bolder" | "lighter" | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800| 900,
                    fontFamily: style.fontFamily,
                    fontSize: style.fontSize as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
                    lineHeight: style.lineHeight as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
                },
                formatter: (params, ticket, callback) => {
                    if (!Array.isArray(params)) {
                        params = [params];
                    }

                    // @ts-ignore
                    const dtFrom = params[0].value[0] as Date;

                    // @ts-ignore
                    const dtTo = params[0].value[2] as Date;

                    return `<b>${intl.formatDate(dtFrom)} ${intl.formatTime(dtFrom)} &dash; ${intl.formatDate(dtTo)} ${intl.formatTime(dtTo)}</b><br />` +
                        params.map((series) => {
                            if (series.seriesName === "Samples") {
                                return series.marker + "&nbsp;" + series.seriesName + ":&nbsp;" + intl.formatNumber(((series.value ?? [0,0]) as number[])[1]);
                            } else {
                                return series.marker + "&nbsp;" + series.seriesName + ":&nbsp;" + temp.format(((series.value ?? [0,0]) as number[])[1]);
                            }
                        }).join("<br />");
                }
            },
            grid: {
                left: 55,
                top: 10,
                right: 20,
                bottom: 30
            },
            textStyle: {
                color: style.color,
                fontStyle: style.fontStyle as "normal" | "italic" | "oblique",
                fontWeight: style.fontWeight as "normal" | "bold" | "bolder" | "lighter" | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800| 900,
                fontFamily: style.fontFamily,
                fontSize: style.fontSize as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
                lineHeight: style.lineHeight as unknown as number, // Probably not (it's number with unit, so string), but whatever, echarts typing is weird.
            },
            series: [],
            animation: false
        }

        if (propsWithDefault.showSamples) {
            if (opts.grid && opts.yAxis && Array.isArray(opts.yAxis)) {
                (opts.grid as EChartOption.Grid).right = 65;
                opts.yAxis.push({
                    name: "Samples",
                    nameLocation: "middle",
                    nameGap: 45,
                    splitLine: {
                        show: false,
                    },
                    axisTick: {
                        lineStyle: {
                            color: style.borderColor,
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: style.borderColor,
                        },
                    },
                })
            }
        }

        if (result) {
            if (propsWithDefault.showAverage) {
                opts.series?.push({
                    type: "line",
                    name: "Average",
                    yAxisIndex: 0,
                    // @ts-ignore  As the typing is wrong, X axis can have date instance as valid argument.
                    data: result.table.map((row) => [new Date(row.from), temperatureContext.converter(row.average), new Date(row.to)]),
                    showSymbol: result.table.length <= 1,
                    smooth: true,
                    lineStyle: {
                        color: "#f39c12",
                    },
                    itemStyle: {
                        color: "#f39c12",
                    },
                });
            }

            if (propsWithDefault.showMin) {
                opts.series?.push({
                    type: "line",
                    name: "Min",
                    yAxisIndex: 0,
                    // @ts-ignore  As the typing is wrong, X axis can have date instance as valid argument.
                    data: result.table.map((row) => [new Date(row.from), temperatureContext.converter(row.min.value), new Date(row.to)]),
                    showSymbol: result.table.length <= 1,
                    smooth: true,
                    lineStyle: {
                        color: "#2980b9",
                    },
                    itemStyle: {
                        color: "#2980b9",
                    },
                });
            }

            if (propsWithDefault.showMax) {
                opts.series?.push({
                    type: "line",
                    name: "Max",
                    yAxisIndex: 0,
                    // @ts-ignore  As the typing is wrong, X axis can have date instance as valid argument.
                    data: result.table.map((row) => [new Date(row.from), temperatureContext.converter(row.max.value), new Date(row.to)]),
                    showSymbol: result.table.length <= 1,
                    smooth: true,
                    lineStyle: {
                        color: "#c0392b",
                    },
                    itemStyle: {
                        color: "#c0392b",
                    },
                });
            }

            if (propsWithDefault.showSamples) {
                opts.series?.push({
                    type: "line",
                    name: "Samples",
                    yAxisIndex: 1,
                    // @ts-ignore  As the typing is wrong, X axis can have date instance as valid argument.
                    data: result.table.map((row) => [new Date(row.from), row.samples, new Date(row.to)]),
                    showSymbol: result.table.length <= 1,
                    smooth: true,
                    lineStyle: {
                        color: "#95a5a6",
                        opacity: 0.3,
                        width: 1,
                    },
                    itemStyle: {
                        color: "#95a5a6",
                    },
                });
            }
        }

        return opts;
    };

    const chartRef = useRef<HTMLDivElement>(null);
    const style = chartRef.current ? window.getComputedStyle(chartRef.current) : undefined;

    return <TemperatureContext.Consumer>{(temperatureContext: TemperatureContext) =>
        <ThemeContext.Consumer>{() =>
            <HistoryChartContainer ref={chartRef}>{
                (style) ? <ReactEChartsCore
                    echarts={echarts}
                    option={options(temperatureContext, style)}
                    notMerge={true}
                    onChartReady={(instance: EChartsInstance) => {
                        if (!result) {
                            instance.showLoading("default", {
                                color: style.borderColor,
                                textColor: style.borderColor,
                                maskColor: "rgba(255, 255, 255, 0)",
                            });
                        }
                    }}
                /> : null
            }</HistoryChartContainer>
        }</ThemeContext.Consumer>
    }</TemperatureContext.Consumer>;
}
